import React, { useState } from "react";
import Image from "next/image";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import PostComposer from "./PostComposer";
import SocialFeed from "./SocialFeed";

const normalizeImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const base = process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:5000";
  return url.startsWith("/") ? `${base}${url}` : `${base}/${url}`;
};

const CampaignGallery = ({ campaign, organization }) => {
  const [expandedImage, setExpandedImage] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { user: currentUser } = useAuth();

  const campaignId = campaign?._id;
  const bannerImages = campaign?.overview?.bannerImageUrl || [];
  const profileImage = campaign?.overview?.profileImageUrl || organization?.logoUrl;

  const allImages = [
    ...(profileImage ? [profileImage] : []),
    ...bannerImages,
  ].filter(Boolean);

  const handlePostCreated = () => {
    setRefreshTrigger((prev) => prev + 1);
    toast.success("Your post has been shared with the community!");
  };

  return (
    <div className="px-4 sm:px-6 pb-6">
      {/* Gallery Grid */}
      {allImages.length > 0 ? (
        <div className="mb-10">
          <h3 className="text-lg font-semibold text-jacarta-700 dark:text-white mb-4">
            Campaign Photos
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {allImages.map((imageUrl, index) => {
              const fullUrl = normalizeImageUrl(imageUrl);
              if (!fullUrl) return null;
              return (
                <div
                  key={index}
                  className="relative aspect-square overflow-hidden rounded-xl cursor-pointer group border border-jacarta-100 dark:border-jacarta-600 hover:border-accent dark:hover:border-accent transition-colors"
                  onClick={() => setExpandedImage(fullUrl)}
                >
                  <Image
                    src={fullUrl}
                    alt={`Campaign photo ${index + 1}`}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.currentTarget.src = "/images/placeholder.jpg";
                    }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                      />
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="mb-10 flex flex-col items-center justify-center py-16 rounded-xl border border-dashed border-jacarta-200 dark:border-jacarta-600 text-jacarta-400 dark:text-jacarta-500">
          <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="text-sm">No photos uploaded yet</p>
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-jacarta-100 dark:border-jacarta-600 mb-8" />

      {/* Comments Section */}
      <div>
        <h3 className="text-lg font-semibold text-jacarta-700 dark:text-white mb-6 text-center">
          Community Comments
        </h3>
        {campaignId ? (
          <div className="max-w-2xl mx-auto">
            <PostComposer
              campaignId={campaignId}
              onPostCreated={handlePostCreated}
              currentUser={currentUser}
            />
            <SocialFeed
              campaignId={campaignId}
              currentUser={currentUser}
              refreshTrigger={refreshTrigger}
            />
          </div>
        ) : (
          <p className="text-jacarta-500 dark:text-jacarta-400 text-sm">
            Campaign information is not available.
          </p>
        )}
      </div>

      {/* Lightbox */}
      {expandedImage && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setExpandedImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-accent transition-colors z-[10000]"
            onClick={() => setExpandedImage(null)}
            aria-label="Close"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div
            className="relative max-w-5xl max-h-[90vh] w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <Image
              src={expandedImage}
              alt="Expanded view"
              width={1920}
              height={1080}
              className="max-w-full max-h-full w-auto h-auto object-contain rounded-xl"
              onError={(e) => { e.currentTarget.src = "/images/placeholder.jpg"; }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignGallery;
